import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const MAX_ATTEMPTS = 3;

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const res = await api.post("/auth/session", { session_id: sessionId });
          setUser(res.data.user);
          window.history.replaceState(null, "", "/");
          navigate("/", { replace: true });
          return;
        } catch (e) {
          const status = e?.response?.status;
          // A Cloudflare 520 / dropped connection / 5xx means the origin was briefly
          // unavailable (pod restarting) — the request never reached our auth code, so
          // the session id is still valid and retrying is safe. A 4xx is a real
          // rejection (invalid session / unauthorized) and must NOT be retried.
          const transient = !status || status >= 500;
          if (transient && attempt < MAX_ATTEMPTS) {
            setRetrying(true);
            await sleep(attempt * 1500);
            continue;
          }
          if (transient) {
            setError("We couldn't reach the sign-in service just now. Please try signing in again.");
          } else {
            const detail = e?.response?.data?.detail;
            setError(typeof detail === "string" ? detail : "Sign in failed. Please try again.");
          }
          return;
        }
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white grid-bg">
      {error ? (
        <div className="max-w-sm text-center px-6" data-testid="auth-callback-error">
          <h2 className="font-heading text-2xl font-bold mb-3">Access denied</h2>
          <p className="text-zinc-500 text-sm mb-6" data-testid="auth-callback-error-message">{error}</p>
          <button
            onClick={() => (window.location.href = "/login")}
            data-testid="auth-callback-signin-button"
            className="bg-zinc-900 text-white px-5 h-10 rounded-sm text-sm font-semibold hover:bg-zinc-800 transition-colors"
          >
            Back to sign in
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4" data-testid="auth-callback-loading">
          <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-zinc-500">{retrying ? "Reconnecting…" : "Signing you in…"}</p>
        </div>
      )}
    </div>
  );
}
